import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
    TrendingUp, TrendingDown, Flag, Link2, Zap, Bell, ArrowRight, Activity, Clock,
    CheckCircle2, AlertTriangle, DollarSign, Users, ArrowUpRight, ArrowDownLeft,
    BarChart3, Bot, Mail, Package, MessageCircle, Settings, Search, FileText, Send,
    RefreshCw, XCircle, CreditCard, Globe, Smartphone, ShieldCheck
} from 'lucide-react';
import { ContextualTour } from '../../components/ContextualTour';
import ActionableStatus, { getDeepLink } from '../../components/ActionableStatus';
import { API } from '../../config/apiBase';


const ADMIN_DASH_STEPS = [
    { target: '[data-testid="dashboard-kpis"]', title: 'KPIs em Tempo Real', content: 'Seus indicadores principais: ofertas, cliques, comunidades e mais. Acompanhe a saude da plataforma.' },
    { target: '[data-testid="dash-finance-card"]', title: 'Financeiro Rapido', content: 'Veja receitas e despesas do mes sem sair do dashboard. Clique para ir ao financeiro completo.' },
    { target: '[data-testid="dash-new-campaign"]', title: 'Nova Campanha', content: 'Crie campanhas de divulgacao direto daqui. Defina mensagem, publico e plataforma de disparo.' },
    { target: '[data-testid="dash-system-health"]', title: 'Saude do Sistema', content: 'Monitore servicos, banco de dados e APIs externas. Tudo verde = tudo funcionando.' },
];

export default function DashboardPage() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [finance, setFinance] = useState(null);
    const [recentEntries, setRecentEntries] = useState([]);
    const [recentDeals, setRecentDeals] = useState([]);
    const [recentRequests, setRecentRequests] = useState([]);
    const [clickStats, setClickStats] = useState({ total_clicks: 0, by_link: [] });
    const [systemHealth, setSystemHealth] = useState(null);
    const [healthLoading, setHealthLoading] = useState(false);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };

    const fetchHealth = useCallback(() => {
        setHealthLoading(true);
        fetch(`${API}/dashboard/system-health`, { headers })
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d) setSystemHealth(d); })
            .catch(() => {})
            .finally(() => setHealthLoading(false));
    }, []);

    useEffect(() => {
        Promise.all([
            fetch(`${API}/dashboard/stats`, { headers }).then(r => r.ok ? r.json() : null),
            fetch(`${API}/ledger/summary`, { headers }).then(r => r.ok ? r.json() : null),
            fetch(`${API}/ledger`, { headers }).then(r => r.ok ? r.json() : null),
            fetch(`${API}/produtos?limit=5`).then(r => r.ok ? r.json() : null),
            fetch(`${API}/offer-requests`, { headers }).then(r => r.ok ? r.json() : null),
            fetch(`${API}/click-stats`, { headers }).then(r => r.ok ? r.json() : null),
        ]).then(([s, f, ledger, deals, requests, clicks]) => {
            if (s) setStats(s);
            if (f) setFinance(f);
            if (ledger) setRecentEntries(ledger.entries?.slice(0, 4) || []);
            if (deals) setRecentDeals(deals.deals?.slice(0, 5) || []);
            if (requests) setRecentRequests(Array.isArray(requests) ? requests.slice(0, 4) : []);
            if (clicks) setClickStats(clicks);
        }).catch(() => {});
        fetchHealth();
    }, []);

    const greeting = () => {
        const h = new Date().getHours();
        if (h < 12) return 'Bom dia';
        if (h < 18) return 'Boa tarde';
        return 'Boa noite';
    };

    const fmt = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    const s = stats || {};
    const f = finance || {};
    const monthlyData = Object.entries(f.monthly || {}).slice(-6);
    const maxVal = Math.max(1, ...monthlyData.flatMap(([, v]) => [v.income || 0, v.expense || 0]));

    return (
        <div className="sv-page" data-testid="dashboard-page">
            <ContextualTour pageKey="admin-dashboard" steps={ADMIN_DASH_STEPS} />
            {/* Hero */}
            <div className="sv-card-system dashboard-hero" style={{ padding: '24px 28px' }}>
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <h1 className="text-xl font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>
                            {greeting()}, {user?.name?.split(' ')[0] || 'Usuario'}
                        </h1>
                        <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
                            Aqui esta o resumo completo do seu sistema
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Link to="/sistema/campanhas?action=nova_campanha" className="sv-btn-gold-system sv-btn-system" data-testid="dash-new-campaign">
                            <Flag size={14} /> Nova Campanha
                        </Link>
                        <Link to="/sistema/gestao?tab=financeiro" className="sv-btn-system" data-testid="dash-go-finance">
                            <DollarSign size={14} /> Financeiro
                        </Link>
                    </div>
                </div>
            </div>

            {/* KPI Principal */}
            <div className="sv-kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }} data-testid="dashboard-kpis">
                <KpiCard label="Ofertas" value={s.total_deals || 0} delta={s.deals_today > 0 ? `+${s.deals_today} hoje` : 'Nenhuma hoje'} tone={s.deals_today > 0 ? 'up' : 'neutral'} icon={Package} />
                <KpiCard label="Campanhas" value={s.total_campaigns || 0} delta="Total ativas" tone="neutral" icon={Flag} />
                <KpiCard label="Usuários" value={s.total_users || 0} delta="Cadastrados" tone="neutral" icon={Users} />
                <KpiCard label="Conexões" value={s.total_connections || 0} delta="Canais" tone="neutral" icon={Link2} />
                <KpiCard label="Automações" value={s.total_automations || 0} delta="Configuradas" tone="neutral" icon={Zap} />
            </div>

            {/* Financeiro Resumo */}
            <div className="sv-bento-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                <div className="sv-card-system" data-testid="dash-finance-card">
                    <div className="sv-card-header">
                        <span className="sv-card-label">Financeiro</span>
                        <Link to="/sistema/gestao" className="text-xs font-medium" style={{ color: 'var(--sv-gold-primary)' }}>
                            Ver tudo <ArrowRight size={12} className="inline" />
                        </Link>
                    </div>
                    {/* Mini KPIs */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="p-3 rounded-xl" style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)' }}>
                            <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--status-green)' }}>Receita</p>
                            <p className="text-base font-bold mt-1" style={{ color: 'var(--status-green)' }}>{fmt(f.categories ? Object.values(f.categories).reduce((a, c) => a + (c.income || 0), 0) : 0)}</p>
                        </div>
                        <div className="p-3 rounded-xl" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)' }}>
                            <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--status-red)' }}>Despesas</p>
                            <p className="text-base font-bold mt-1" style={{ color: 'var(--status-red)' }}>{fmt(f.categories ? Object.values(f.categories).reduce((a, c) => a + (c.expense || 0), 0) : 0)}</p>
                        </div>
                        <div className="p-3 rounded-xl" style={{ background: 'rgba(var(--sv-accent-rgb), 0.06)', border: '1px solid rgba(var(--sv-accent-rgb), 0.15)' }}>
                            <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--sv-gold-primary)' }}>Saldo</p>
                            <p className="text-base font-bold mt-1" style={{ color: 'var(--sv-gold-primary)' }}>{fmt(f.categories ? Object.values(f.categories).reduce((a, c) => a + (c.income || 0) - (c.expense || 0), 0) : 0)}</p>
                        </div>
                    </div>
                    {/* Últimos lançamentos */}
                    <div className="flex flex-col gap-1">
                        {recentEntries.length > 0 ? recentEntries.map(e => (
                            <div key={e.ledger_id} className="flex items-center justify-between py-2" style={{ borderBottom: '1px solid var(--border)' }}>
                                <div className="flex items-center gap-2 min-w-0">
                                    {e.type === 'income' ? <ArrowUpRight size={12} style={{ color: 'var(--status-green)' }} /> : <ArrowDownLeft size={12} style={{ color: 'var(--status-red)' }} />}
                                    <span className="text-sm truncate" style={{ color: 'var(--text)' }}>{e.description}</span>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'var(--surface-2)', color: 'var(--muted-2)' }}>{e.category}</span>
                                </div>
                                <span className="text-sm font-bold shrink-0 ml-2" style={{ color: e.type === 'income' ? 'var(--status-green)' : 'var(--status-red)' }}>
                                    {e.type === 'income' ? '+' : '-'}{fmt(e.value)}
                                </span>
                            </div>
                        )) : (
                            <p className="text-xs py-3 text-center" style={{ color: 'var(--muted)' }}>Nenhum lançamento</p>
                        )}
                    </div>
                    {(f.pending?.length || 0) > 0 && (
                        <div className="mt-3 p-2.5 rounded-lg flex items-center gap-2" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
                            <AlertTriangle size={14} style={{ color: 'var(--status-amber)' }} />
                            <span className="text-xs font-medium" style={{ color: 'var(--status-amber)' }}>{f.pending.length} pendente(s)</span>
                        </div>
                    )}
                </div>

                {/* Evolucao Mensal */}
                <div className="sv-card-system" data-testid="dash-monthly-chart">
                    <div className="sv-card-header">
                        <span className="sv-card-label">Evolucao Mensal</span>
                    </div>
                    {monthlyData.length === 0 ? (
                        <p className="text-sm py-8 text-center" style={{ color: 'var(--muted)' }}>Sem dados suficientes</p>
                    ) : (
                        <div className="flex flex-col gap-3">
                            {monthlyData.map(([month, vals]) => {
                                const net = (vals.income || 0) - (vals.expense || 0);
                                return (
                                    <div key={month}>
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-xs font-medium" style={{ color: 'var(--muted)' }}>{month}</span>
                                            <span className="text-xs font-bold" style={{ color: net >= 0 ? 'var(--status-green)' : 'var(--status-red)' }}>{fmt(net)}</span>
                                        </div>
                                        <div className="flex gap-1 h-4">
                                            <div className="rounded-r" style={{ width: `${((vals.income || 0) / maxVal) * 100}%`, background: 'var(--status-green)', minWidth: (vals.income || 0) > 0 ? 4 : 0, transition: 'width 0.5s ease' }} />
                                            <div className="rounded-r" style={{ width: `${((vals.expense || 0) / maxVal) * 100}%`, background: 'var(--status-red)', minWidth: (vals.expense || 0) > 0 ? 4 : 0, transition: 'width 0.5s ease' }} />
                                        </div>
                                    </div>
                                );
                            })}
                            <div className="flex gap-4 text-[10px]" style={{ color: 'var(--muted-2)' }}>
                                <span><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: 'var(--status-green)' }} />Receita</span>
                                <span><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: 'var(--status-red)' }} />Despesa</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Atividade Recente */}
                <div className="sv-card-system" data-testid="dash-recent-deals">
                    <div className="sv-card-header">
                        <span className="sv-card-label">Ofertas Recentes</span>
                        <Link to="/sistema/alertas" className="text-xs font-medium" style={{ color: 'var(--sv-gold-primary)' }}>
                            Ver tudo <ArrowRight size={12} className="inline" />
                        </Link>
                    </div>
                    <div className="flex flex-col gap-1">
                        {recentDeals.length > 0 ? recentDeals.map((deal, i) => (
                            <div key={deal.deal_id || i} className="flex items-center gap-3 py-2.5" style={{ borderBottom: '1px solid var(--border)' }}>
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'var(--surface-2)' }}>
                                    {deal.image ? <img src={deal.image} alt="" className="w-8 h-8 rounded-lg object-cover" /> : <Package size={14} style={{ color: 'var(--sv-gold-primary)' }} />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{deal.title}</p>
                                    <p className="text-[10px]" style={{ color: 'var(--muted-2)' }}>{deal.store || deal.category}</p>
                                </div>
                                {deal.price && <span className="text-sm font-bold shrink-0" style={{ color: 'var(--sv-gold-primary)' }}>R$ {deal.price}</span>}
                            </div>
                        )) : (
                            <p className="text-sm py-4 text-center" style={{ color: 'var(--muted)' }}>Nenhuma oferta recente</p>
                        )}
                    </div>
                </div>

                {/* Pedidos Recentes */}
                {recentRequests.length > 0 && (
                    <div className="sv-card-system" data-testid="dash-recent-requests">
                        <div className="sv-card-header">
                            <span className="sv-card-label">Pedidos de Oferta</span>
                            <Link to="/sistema/pedidos" className="text-xs font-medium" style={{ color: 'var(--sv-gold-primary)' }}>
                                Ver tudo <ArrowRight size={12} className="inline" />
                            </Link>
                        </div>
                        <div className="flex flex-col gap-1">
                            {recentRequests.map(r => (
                                <div key={r.request_id} className="flex items-center gap-3 py-2" style={{ borderBottom: '1px solid var(--border)' }}>
                                    <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: r.status === 'found' ? 'var(--status-success-bg)' : 'var(--status-warning-bg)' }}>
                                        <Package size={12} style={{ color: r.status === 'found' ? 'var(--status-green)' : 'var(--status-amber)' }} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{r.product_name}</p>
                                        <p className="text-[10px]" style={{ color: 'var(--muted-2)' }}>{r.category} {r.max_price ? `- até R$ ${r.max_price}` : ''}</p>
                                    </div>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full shrink-0" style={{ background: r.status === 'found' ? 'var(--status-success-bg)' : r.status === 'expired' ? 'var(--status-error-bg)' : 'var(--status-warning-bg)', color: r.status === 'found' ? 'var(--status-green)' : r.status === 'expired' ? 'var(--status-red)' : 'var(--status-amber)' }}>
                                        {r.status === 'found' ? 'Encontrado' : r.status === 'expired' ? 'Expirado' : 'Pendente'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Saúde do Sistema - Widget Expandido */}
                <div className="sv-card-system" data-testid="dash-system-health" style={{ gridColumn: 'span 1' }}>
                    <div className="sv-card-header">
                        <span className="sv-card-label">Saude do Sistema</span>
                        <div className="flex items-center gap-2">
                            <button onClick={fetchHealth} disabled={healthLoading} className="p-1 rounded hover:bg-[rgba(128,128,128,0.08)] transition-colors" data-testid="health-refresh-btn">
                                <RefreshCw size={12} className={healthLoading ? 'animate-spin' : ''} style={{ color: 'var(--muted-2)' }} />
                            </button>
                            <Link to="/sistema/admin/master?tab=integracoes" className="text-xs font-medium" style={{ color: 'var(--sv-gold-primary)' }}>
                                Admin Master <ArrowRight size={10} className="inline" />
                            </Link>
                        </div>
                    </div>
                    {systemHealth ? (
                        <>
                            {/* Overall Status Bar */}
                            <div className="flex items-center gap-3 mb-4 p-3 rounded-xl" style={{
                                background: systemHealth.overall === 'healthy' ? 'var(--status-success-bg)' : systemHealth.overall === 'degraded' ? 'var(--status-error-bg)' : 'var(--status-warning-bg)',
                                border: `1px solid ${systemHealth.overall === 'healthy' ? 'var(--status-success-border)' : systemHealth.overall === 'degraded' ? 'var(--status-error-border)' : 'var(--status-warning-border)'}`,
                            }}>
                                <ShieldCheck size={16} style={{ color: systemHealth.overall === 'healthy' ? 'var(--status-green)' : systemHealth.overall === 'degraded' ? 'var(--status-red)' : 'var(--status-amber)' }} />
                                <div className="flex-1">
                                    <p className="text-xs font-bold" style={{ color: systemHealth.overall === 'healthy' ? 'var(--status-green)' : systemHealth.overall === 'degraded' ? 'var(--status-red)' : 'var(--status-amber)' }}>
                                        {systemHealth.overall === 'healthy' ? 'Sistema Saudavel' : systemHealth.overall === 'degraded' ? 'Atencao Necessaria' : 'Configuracao Parcial'}
                                    </p>
                                    <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted-2)' }}>
                                        {systemHealth.online} online · {systemHealth.configured} configurado · {systemHealth.not_configured} pendente
                                    </p>
                                </div>
                                <span className="text-[9px]" style={{ color: 'var(--muted-2)' }}>
                                    {new Date(systemHealth.checked_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                            {/* Individual Checks */}
                            <div className="flex flex-col gap-1">
                                {systemHealth.checks.map(check => (
                                    <HealthCheckRow key={check.key} check={check} />
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="py-6 text-center">
                            <RefreshCw size={16} className="animate-spin mx-auto mb-2" style={{ color: 'var(--muted-2)' }} />
                            <p className="text-xs" style={{ color: 'var(--muted)' }}>Verificando integrações...</p>
                        </div>
                    )}
                </div>

                {/* Tracking de Cliques */}
                {clickStats.total_clicks > 0 && (
                    <div className="sv-card-system" data-testid="dash-click-stats">
                        <div className="sv-card-header">
                            <span className="sv-card-label">Cliques nos Botões ({clickStats.total_clicks})</span>
                        </div>
                        <div className="flex flex-col gap-2">
                            {clickStats.by_link?.map((lk, i) => (
                                <div key={lk.link_id} className="flex items-center justify-between py-2" style={{ borderBottom: '1px solid var(--border)' }}>
                                    <div className="flex items-center gap-2">
                                        <Activity size={12} style={{ color: 'var(--sv-gold-primary)' }} />
                                        <span className="text-sm" style={{ color: 'var(--text)' }}>{lk.link_name}</span>
                                        <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'var(--surface-2)', color: 'var(--muted-2)' }}>{lk.platform_type}</span>
                                    </div>
                                    <span className="text-sm font-bold" style={{ color: 'var(--sv-gold-primary)' }}>{lk.clicks}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Ações Rápidas */}
                <div className="sv-card-system" data-testid="dash-quick-actions">
                    <div className="sv-card-header"><span className="sv-card-label">Ações Rápidas</span></div>
                    <div className="grid grid-cols-2 gap-2">
                        <QAction icon={Flag} label="Nova Campanha" to="/sistema/campanhas?action=nova_campanha" />
                        <QAction icon={DollarSign} label="Novo Lançamento" to="/sistema/gestao?tab=financeiro" />
                        <QAction icon={FileText} label="Novo Template" to="/sistema/templates?action=novo_template" />
                        <QAction icon={Search} label="Pesquisas" to="/sistema/pesquisas" />
                        <QAction icon={Link2} label="Conexões" to="/sistema/conexoes" />
                        <QAction icon={Zap} label="Automações" to="/sistema/automacoes" />
                    </div>
                </div>

                {/* Categorias financeiras */}
                {f.categories && Object.keys(f.categories).length > 0 && (
                    <div className="sv-card-system" data-testid="dash-categories">
                        <div className="sv-card-header">
                            <span className="sv-card-label">Por Categoria</span>
                            <Link to="/sistema/gestao?tab=contas" className="text-xs font-medium" style={{ color: 'var(--sv-gold-primary)' }}>
                                Detalhes <ArrowRight size={12} className="inline" />
                            </Link>
                        </div>
                        <div className="flex flex-col gap-2">
                            {Object.entries(f.categories).map(([cat, vals]) => {
                                const net = (vals.income || 0) - (vals.expense || 0);
                                return (
                                    <div key={cat} className="flex items-center justify-between py-2" style={{ borderBottom: '1px solid var(--border)' }}>
                                        <span className="text-sm" style={{ color: 'var(--text)' }}>{cat}</span>
                                        <span className="text-sm font-bold" style={{ color: net >= 0 ? 'var(--status-green)' : 'var(--status-red)' }}>{fmt(net)}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function KpiCard({ label, value, delta, tone, icon: Icon }) {
    return (
        <div className="sv-kpi" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center justify-between mb-1">
                <span className="sv-kpi-label">{label}</span>
                <Icon size={14} style={{ color: 'var(--sv-gold-primary)', opacity: 0.5 }} />
            </div>
            <span className="sv-kpi-value">{value}</span>
            <span className={`sv-kpi-delta ${tone}`}>
                {tone === 'up' && <TrendingUp size={12} className="inline mr-1" />}
                {tone === 'down' && <TrendingDown size={12} className="inline mr-1" />}
                {delta}
            </span>
        </div>
    );
}

function QAction({ icon: Icon, label, to }) {
    return (
        <Link to={to} className="flex items-center gap-2.5 p-3 rounded-xl transition-all hover:scale-[1.02]"
            style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(var(--sv-accent-rgb), 0.1)' }}>
                <Icon size={14} style={{ color: 'var(--sv-gold-primary)' }} />
            </div>
            <span className="text-xs font-medium" style={{ color: 'var(--text)' }}>{label}</span>
        </Link>
    );
}

const HEALTH_ICON_MAP = {
    api_backend: Activity,
    database: BarChart3,
    telegram: Bot,
    email_smtp: Mail,
    stripe: CreditCard,
    google_login: Globe,
    facebook_login: Smartphone,
    belvo: ShieldCheck,
};

const HEALTH_DEEP_LINKS = {
    api_backend: '/sistema/admin/master?tab=integracoes',
    database: '/sistema/admin/master?tab=integracoes',
    telegram: '/sistema/admin/master?tab=telegram',
    email_smtp: '/sistema/admin/master?tab=integracoes&group=Email+%2F+SMTP',
    stripe: '/sistema/admin/master?tab=integracoes&group=Stripe+%2F+Pagamentos',
    google_login: '/sistema/admin/master?tab=social',
    facebook_login: '/sistema/admin/master?tab=social',
    belvo: '/sistema/admin/master?tab=integracoes&group=Belvo+%2F+Open+Finance',
};

const HEALTH_STATUS_STYLE = {
    online: { color: 'var(--status-green)', icon: CheckCircle2, label: 'Online' },
    configured: { color: 'var(--sv-gold-primary)', icon: CheckCircle2, label: 'Configurado' },
    degraded: { color: 'var(--status-amber)', icon: AlertTriangle, label: 'Degradado' },
    error: { color: 'var(--status-red)', icon: XCircle, label: 'Erro' },
    not_configured: { color: 'var(--muted-2)', icon: Clock, label: 'Pendente' },
};

function HealthCheckRow({ check }) {
    const Icon = HEALTH_ICON_MAP[check.key] || Activity;
    const statusCfg = HEALTH_STATUS_STYLE[check.status] || HEALTH_STATUS_STYLE.not_configured;
    const StatusIcon = statusCfg.icon;
    const deepLink = HEALTH_DEEP_LINKS[check.key];

    const row = (
        <div className="flex items-center justify-between py-2 group" style={{ borderBottom: '1px solid var(--border)' }} data-testid={`health-check-${check.key}`}>
            <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0" style={{ background: `${statusCfg.color}15` }}>
                    <Icon size={12} style={{ color: statusCfg.color }} />
                </div>
                <div className="min-w-0">
                    <span className="text-xs font-medium block" style={{ color: 'var(--text)' }}>{check.label}</span>
                    <span className="text-[9px] block" style={{ color: 'var(--muted-2)' }}>{check.detail}</span>
                </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
                <StatusIcon size={11} style={{ color: statusCfg.color }} />
                <span className="text-[10px] font-semibold" style={{ color: statusCfg.color }}>{statusCfg.label}</span>
            </div>
        </div>
    );

    if (deepLink) return <Link to={deepLink} className="block hover:bg-[rgba(128,128,128,0.04)] rounded transition-colors">{row}</Link>;
    return row;
}
